/* WorkTriggerDetailsViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./WorkTriggerDetailsViewRenderer");
const ScheduleComponent = require("@gluenetworks/schedule_component");
const {isSystemTriggerType, readManagedBy} = require("../../../../lib/managedSchedules");

const _ = require("lodash");
_.mixin(require("underscore.string").exports());

class WorkTriggerDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        MUF,
        router,
        ControlUser,
        CustomFieldEntityDef,
        SiteEntity,
        ConstructType
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._ControlUser = ControlUser;
        this._customFieldEntityDef = CustomFieldEntityDef;
        this._siteEntityDef = SiteEntity;
        this._constructTypeEntityDef = ConstructType;
        this._nodeProps = {};
        this._sitePaths = [];
        this._osMap = undefined;
        this._vendorMap = undefined;
        this._typeBaseMap = undefined;
        this._updatedBySelf = false;
        this._triggerType = this._gameEditor._triggerType;
        this._isNotificationsBlockValid = true;
        this._triggerWorkType = this._triggerType.workType;

        const triggerTypeEditable = _.get(
            this._triggerType.getWorkInput(),
            "syncOptions.isEditable",
            false
        );

        // First two conditions only apply to older created schedules.
        // To be removed in future releases.
        this._isUserActionSync =
            this._triggerType.getDisplayName().search("Sync from Device Manager") >= 0 ||
            this._triggerType.getDisplayName().search("Preview Sync from Settings") >= 0 ||
            triggerTypeEditable;
        this._managedBy = readManagedBy(this._triggerType);
        if (
            isSystemTriggerType({
                workType: this._triggerWorkType,
                isUserActionSync: this._isUserActionSync,
                managedBy: this._managedBy,
            })
        ) {
            this._isSystemTriggerType = true;
            this._router = router;
        } else {
            this._triggerTypeFeed = undefined;
            //this._workType = undefined;
            this._workTypes = {};
            this._targets = {count: 0, workingInstance: null};
            this._notifyMe = undefined;
            this._isSystemTriggerType = false;
            this._modalUserFeedback = MUF;
        }

        this._osPlan = undefined;
        this._targetCount = undefined;

        this._renderer = new Renderer(this);

        if (this._isSystemTriggerType === true) {
            this._registerListeners();
            this.hideViewActions();
        }
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerTriggerTypeListeners() {
        this._triggerType.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        this._triggerType.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterTriggerTypeListeners() {
        this._triggerType.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._triggerType.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    notificationsValidationChanged(isValid) {
        this.isNotificationsBlockValid = isValid;
        if (isValid) {
            this.enableViewActions();
        } else {
            this.disableViewActions();
        }
    }

    set isNotificationsBlockValid(isValid) {
        this._isNotificationsBlockValid = isValid;
    }

    get isNotificationsBlockValid() {
        return this._isNotificationsBlockValid;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    destroyObjects() {
        this._gameEditor = undefined;
        this._logger = undefined;
        this._renderer = undefined;
        this._triggerType = undefined;
        this._isSystemTriggerType = undefined;
        this._managedBy = undefined;
        this._osPlan = undefined;
    }

    shutdown() {
        return this._renderer
            .shutdown()
            .then(() => {
                if (!this._isSystemTriggerType) {
                    this._unregisterListeners();
                    this._unregisterTriggerTypeListeners();
                    this._modalUserFeedback = undefined;
                    this._ControlUser = undefined;
                    this._updatedBySelf = undefined;
                    this._vendorMap = undefined;
                    this._typeBaseMap = undefined;
                    this._siteEntityDef = undefined;
                    this._sitePaths = undefined;
                    this._constructTypeEntityDef = undefined;
                    this._nodeProps = undefined;
                    this.destroyObjects();
                    //this._workType = undefined;
                    this._workTypes = undefined;
                } else {
                    this.destroyObjects();
                    this._router = undefined;
                }
            })
            .catch((err) => {
                this._logger.warn({error: err}, "WorkTriggerDetailsViewComponent.shutdown() error");
                //console.error(err);
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START EDITOR METHODS                    */
    /*******************************************/

    resizeContainer(width, height) {
        this.emit("ResizeContainer", width, height);
    }

    /*******************************************/
    /* END EDITOR METHODS                      */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged() {
        this.emit("Dirty");
        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this.saveTriggerType().then(() => undefined); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._triggerType.clearPendingPatches(true))
                            .then(() => this._renderer._onTriggerTypeDetailsCancelAction())
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => undefined);
                    } else {
                        this._logger.warn(
                            {error: err},
                            "WorkTriggerDetailsViewComponent.onBeforeUnloadFunc() error"
                        );
                        //console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this._triggerType.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
            this.showViewActions();
        } else {
            this._gameEditor.clearOnBeforeUnload();
            this.hideViewActions();
        }
    }

    _onPristinePropertiesChanged(inputData, oldData) {
        if (!this._updatedBySelf) {
            this.emit("TriggerTypePropertiesChanged", inputData, oldData);
        } else {
            this._updatedBySelf = false;
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*** triggerType ***/
    get triggerType() {
        return this.getTriggerType();
    }

    getTriggerType() {
        return this._triggerType;
    }

    setTriggerTypeProperty(name, value) {
        if (name === "workInput.captureNewSnapShots") {
            let workInp = this._triggerType.getWorkInput();
            if (value) {
                workInp.captureNewSnapShots = true;
                this._triggerType.setWorkInput(workInp);
            } else {
                const _capSnapShotVal = _.get(
                    this._triggerType,
                    "_pristineProperties.workInput.captureNewSnapShots"
                );
                if (_capSnapShotVal === false) {
                    workInp.captureNewSnapShots = false;
                } else {
                    delete workInp.captureNewSnapShots;
                }
                this._triggerType.setWorkInput(workInp);
            }
        } else if (name === "workInput.snapshotName") {
            let workInp = this._triggerType.getWorkInput();
            if (value) {
                workInp.snapshotName = value;
                this._triggerType.setWorkInput(workInp);
            } else {
                const _capSnapShotVal = _.get(
                    this._triggerType,
                    "_pristineProperties.workInput.snapshotName"
                );
                if (_capSnapShotVal === "") {
                    workInp.snapshotName = "";
                } else {
                    delete workInp.snapshotName;
                }
                this._triggerType.setWorkInput(workInp);
            }
        } else {
            this._triggerType[name] = value;
        }
    }

    /*** workType ***/
    get workType() {
        return this.getWorkType();
    }

    getWorkType() {
        return _.get(this._triggerType, "workType");
    }

    set workType(w) {
        return this.setWorkType(w);
    }

    get subWorkType() {
        return _.get(this._triggerType, "subWorkType");
    }

    /* ? do we really need a setter for work type
    setWorkType(w, silent) {
        let old = this._workType;
        this._workType = w;
        this._triggerType.setWorkTypeDef(this._workType);
        if (!silent) {
            this.emit("WorkTypeChanged", this._workType, old);
        }
        return this;
    }*/

    _setDefaultWorkType() {
        // this._workType = this._workTypes[Object.keys(this._workTypes)[0]];
    }

    /*** schedule ***/
    get schedule() {
        return this.getSchedule();
    }

    getSchedule() {
        return _.get(this._triggerType, "schedule");
    }

    set schedule(s) {
        return this.setSchedule(s);
    }

    setSchedule(s, silent) {
        if (!this._triggerType) {
            return this;
        }
        let old = this._triggerType.getSchedule();
        if (!_.isEqual(s, old)) {
            this._triggerType.setSchedule(s);
            if (!silent) {
                this.emit("ScheduleChanged", s, old);
            }
        }
        return this;
    }

    _setDefaultTriggerType(silent) {
        this.setSchedule(ScheduleComponent.defaultValue(), silent);
    }

    /*** workInput ***/
    get workInput() {
        return this.getWorkInput();
    }

    getWorkInput() {
        return this._triggerType.getWorkInput() || {};
    }

    set workInput(i) {
        return this.setWorkInput(i);
    }

    setWorkInput(i, silent) {
        let old = this.workInput;
        this._triggerType.setWorkInput(i);
        if (!silent) {
            this.emit("WorkInputChanged", i, old);
        }
        return this;
    }

    /*** workTarget ***/
    get workTarget() {
        return this.getWorkTarget();
    }

    getWorkTarget() {
        return _.get(this._triggerType, "workTarget");
    }

    set workTarget(t) {
        return this.setWorkTarget(t);
    }

    setWorkTarget(t, silent) {
        let old = this._triggerType.getWorkTarget();
        if (!_.isEqual(t, old)) {
            this._triggerType.setWorkTarget(t);
            let workTargetType = this.mapWorkTargetToTargetType(t);
            this.setWorkTargetType(workTargetType, silent);
            if (!silent) {
                this.emit("WorkTargetChanged", t, old);
            }
        }
        return this;
    }

    /*** workTarget ***/
    get notifyMe() {
        return this.getNotifyMe();
    }

    getNotifyMe() {
        return _.get(this._triggerType.getWorkInput(), "notifyMe", false);
    }

    set notifyMe(newVal) {
        return this.setNotifyMe(newVal);
    }

    setNotifyMe(newWorkInput /*, silent*/) {
        const oldWorkInput = this.fixWorkInput(this._triggerType.getWorkInput());

        if (!_.isEqual(newWorkInput, oldWorkInput)) {
            const combinedWorkInput = this._combineWorkInput(newWorkInput);
            this._notifyMe = combinedWorkInput;
            //_.set(workInput, "notifyMe", newVal);
            this._triggerType.setWorkInput(combinedWorkInput);
            /*
            if (!silent) {
                this.emit('WorkTargetChanged', t, old);
            }*/
        }
        return this;
    }

    get osPlan() {
        return this._osPlan;
    }

    set osPlan(newOsPlan) {
        this._osPlan = newOsPlan;
    }

    get countOsPlanDevices() {
        return this._osPlan.deviceCount;
    }

    /**
     * Normalize workInput object
     */
    fixWorkInput(workInput) {
        let fixedWorkInput = {};
        if (workInput.notifyMe && _.isString(workInput.notifyMe)) {
            fixedWorkInput.notifyMe = workInput.notifyMe;
        }

        if (workInput.notifyMe && _.isBoolean(workInput.notifyMe)) {
            //silently change old notifyMe === true values?
            //fixedWorkInput.notifyMe = this.model.user
        }
        if (workInput.notifyEmail && _.isArray(workInput.notifyEmail)) {
            fixedWorkInput.notifyEmail = workInput.notifyEmail;
        }
        if (workInput.notifyActionGroupDefault && _.isString(workInput.notifyActionGroupDefault)) {
            fixedWorkInput.notifyActionGroupDefault = workInput.notifyActionGroupDefault;
        }
        return fixedWorkInput;
    }

    /**
     * Combine workInput object before saving, to make sure it will not lost some additional params comes from ModelEditor
     */
    _combineWorkInput(newWorkInput) {
        let oldWorkInput = this._triggerType.getWorkInput();
        if (oldWorkInput.notifyMe) {
            delete oldWorkInput.notifyMe;
        }
        if (oldWorkInput.notifyEmail) {
            delete oldWorkInput.notifyEmail;
        }
        if (oldWorkInput.notifyActionGroupDefault) {
            delete oldWorkInput.notifyActionGroupDefault;
        }
        return _.merge(oldWorkInput, newWorkInput);
    }

    /*** workTarget count ***/
    getWorkTargetCount(typeAttached = false) {
        let workTarget = this._triggerType.getWorkTarget();

        if (workTarget) {
            if (workTarget.ids && workTarget.ids.length) {
                return this.attachType(workTarget, workTarget.ids.length, typeAttached);
            } else if (workTarget.id) {
                return this.attachType(workTarget, 1, typeAttached);
            } else if (this.mapWorkTargetToTargetType(workTarget) === "ALL") {
                return "All";
            } else if (this._targetCount !== undefined) {
                return this.attachType(workTarget, this._targetCount, typeAttached);
            } else if (this.osPlan) {
                return this.attachType(workTarget, this.countOsPlanDevices, typeAttached);
            }
        }
        return 0;
    }

    attachType(workTarget, count, typeAttached) {
        if (typeAttached) {
            if (workTarget.entityType === "OsUpgradePlan") {
                return `${count} OS Plan${count > 1 ? "s" : ""}`;
            } else {
                return `${count} device${count > 1 ? "s" : ""}`;
            }
        }
        return count;
    }

    getDevicesCountByQuery(query) {
        return this.targetEntityClass.queryDeviceCount(this.gameEditor.getActiveOrgId(), query);
    }

    _setDefaultWorkTarget(silent) {
        this.setWorkTarget(this.mapWorkTargetTypeToTarget(), silent);
    }

    /*** workTargetType ***/
    get workTargetType() {
        return this.getWorkTargetType();
    }

    getWorkTargetType() {
        return this._workTargetType;
    }

    set workTargetType(t) {
        return this.setWorkTargetType(t);
    }

    setWorkTargetType(t, silent) {
        let old = this.workTargetType;
        this._workTargetType = t;
        if (!silent) {
            this.emit("WorkTargetTypeChanged", t, old);
        }
        return this;
    }

    /*** targetName ***/
    get targetName() {
        return this.getTargetName();
    }

    getTargetName() {
        return this.getTargetEntityType();
    }

    /*** targetEntityClass ***/
    get targetEntityClass() {
        return this.getTargetEntityClass();
    }

    getTargetEntityClass() {
        let entityType = this.getTargetEntityType();
        if (entityType === "Device") {
            return this._gameEditor.getDeviceEntityClass();
        } else if (_.get(this._triggerType.getWorkTarget(), "category") === "construct") {
            this._isConstruct = true;
            return this._gameEditor.getDeviceEntityClass();
        } else {
            throw new Error(`The work target entity type of ${entityType} is not supported`);
        }
    }

    getOSUpgradePlanEntityClass() {
        return this._gameEditor.getOsUpgradePlanEntity();
    }

    getTargetEntityType() {
        return _.upperFirst(
            _.get(
                this._triggerType,
                ["workTarget", "baseEntityType"],
                _.get(this._triggerType, ["workTarget", "entityType"])
            )
        );
    }

    /*** work types ***/
    get workTypes() {
        return this.getWorkTypes();
    }

    getWorkTypes() {
        return this._workTypes;
    }

    /*** notifTypeDef ***/
    get notifTypeDef() {
        return this.getNotifTypeDef();
    }

    getNotifTypeDef() {
        return _.get(
            this._triggerType,
            "constructPropertyNotification",
            _.get(this._triggerType, "entityNotification")
        );
    }

    set notifTypeDef(n) {
        return this.setNotifTypeDef(n);
    }

    setNotifTypeDef(n, silent) {
        let old =
            this._triggerType.getConstructPropertyNotification() ||
            this._triggerType.getEntityNotification();
        if (n.notifConstruct) {
            this._triggerType.setConstructPropertyNotification(n);
        } else {
            this._triggerType.setEntityNotification(n);
        }
        if (!silent) {
            this.emit("NotifTypeDefChanged", n, old);
        }
        return this;
    }
    /*** solution ***/
    get solution() {
        return this.getSolution();
    }

    getSolution() {
        return _.get(this._triggerType, "solution");
    }

    /*** Editor Mode ***/
    get editorMode() {
        return this.getEditorMode();
    }

    getEditorMode() {
        return this._gameEditor.getInstanceEditorStateProperty("details.editorMode") || "general";
    }

    set editorMode(editorState) {
        return this.setEditorMode(editorState);
    }

    setEditorMode(editorState, silent) {
        let old = this.getEditorMode();
        this._gameEditor.setInstanceEditorStateProperty("details.editorMode", editorState);
        if (!silent) {
            this.emit("EditorModeChanged", editorState, old);
        }
        return this;
    }

    isDirty() {
        return this._triggerType.isDirty();
    }

    /*** Game Editor ***/
    get gameEditor() {
        return this.getGameEditor();
    }

    getGameEditor() {
        return this._gameEditor;
    }

    mapWorkTargetTypeToTarget(workTargetType) {
        if (!workTargetType || !this.workType) {
            return {
                category: "construct",
                construct: "node",
                method: "multi",
                ids: [],
            };
        }
        let workTarget = {};
        if (this.workType.targetConstruct) {
            workTarget.category = "construct";
            workTarget.construct = this.workType.targetConstruct;
        } else if (this.workType.targetEntityType) {
            workTarget.category = "entity";
            workTarget.entityType = this.workType.targetEntityType;
        }
        switch (workTargetType) {
            case "EVENT":
                workTarget.method = "event";
                break;
            case "ALL":
                workTarget.method = "query";
                workTarget.query = {};
                break;
            case "SPECIFIC":
                workTarget.method = "multi";
                workTarget.ids = [];
                break;
            case "NODE_TYPE":
                workTarget.method = "query";
                workTarget.query = {typeName: ""};
                break;
            case "ASSOCIATED_NODES":
                workTarget.method = "associatedNodes";
                workTarget.id = "";
                break;
        }
        return workTarget;
    }

    mapWorkTargetToTargetType(workTarget) {
        if (workTarget) {
            switch (workTarget.method) {
                case "event":
                    return "EVENT";
                case "query":
                    if (_.isEmpty(workTarget.query)) {
                        return "ALL";
                    } else if (workTarget.query.typeName) {
                        return "NODE_TYPE";
                    }
                    break;
                case "single":
                case "multi":
                    return "SPECIFIC";
                case "associatedNodes":
                    return "ASSOCIATED_NODES";
            }
        }
        return "SPECIFIC";
    }

    whenTriggerTypeIsReady() {
        return this._gameEditor
            .whenTriggerTypeIsReady()
            .then(() => this._getNodeProps())
            .then(() => this._getVendorMapDynamic())
            .then(() => this._getTypeBaseMapDynamic())
            .then(() => {
                return this._siteEntityDef.loadSites(
                    this.gameEditor.getActiveOrgId(),
                    {},
                    {
                        id: true,
                        path: true,
                        name: true,
                        locationType: true,
                    },
                    {
                        path: 1,
                    }
                );
            })
            .then((sitePaths) => {
                this._sitePaths = sitePaths;
                return Promise.resolve();
            })
            .then(() => {
                let targetMethod, workTarget;

                this._triggerType = this._gameEditor.getTriggerType();
                workTarget = this.triggerType.workTarget;
                targetMethod = workTarget.method;

                if (!this.triggerType.triggerType) {
                    this._setDefaultTriggerType(true);
                }
                if (!workTarget) {
                    this._setDefaultWorkTarget(true);
                }
                this._notifyMe = this.getNotifyMe() ? this.getNotifyMe() : false;
                this._registerTriggerTypeListeners();
                if (targetMethod === "query" && !_.isEmpty(workTarget.query)) {
                    // setTimeout(() => {
                    this.getDevicesCountByQuery(workTarget.query).then((targetCnt) => {
                        if (targetCnt) {
                            this._targetCount = targetCnt;
                        }
                        // console.log(this.editorMode);
                        if (this._renderer._tabComponents.general) {
                            this.emit("WorkTargetChanged");
                            // this._renderer._tabComponents.general.renderer._onWorkTargetChanged();
                        }
                    });

                    // }, 5000);
                } else if (targetMethod === "related") {
                    const OsPlanEntity = this._gameEditor.getOsUpgradePlanEntity();
                    return OsPlanEntity.loadOsUpgradePlan(
                        this.gameEditor.getActiveOrgId(),
                        workTarget.baseEntityId
                    ).then((osPlan) => {
                        this.osPlan = osPlan;
                        return Promise.resolve();
                    });
                }
                return Promise.resolve();
            })
            .then(() => {
                return this.getOsMap();
            });
    }

    whenWorkTypeIsReady() {
        return Promise.resolve();
    }

    resetTriggerTypeProperties() {
        this._triggerType.clearPendingPatches();
        return Promise.resolve();
    }

    saveTriggerType() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._triggerType.isDirty()) {
                    this._triggerType.isExhausted = false;
                    this._converted = false;
                    this._updatedBySelf = true;
                    return this._triggerType.update();
                }
            })
            .catch((err) => {
                this._modalUserFeedback.displayError(err);
                this._logger.warn(
                    {error: err},
                    "TriggerTypeDetailsViewComponent.saveTriggerType() error."
                );
            });
    }

    disableViewActions() {
        this._renderer._disableViewActions();
    }

    enableViewActions() {
        this._renderer._enableViewActions();
    }

    showViewActions() {
        this._renderer._showViewActions();
    }

    hideViewActions() {
        this._renderer._hideViewActions();
    }

    get disabled() {
        return this._triggerType &&
            this._triggerType.isExhausted &&
            this._triggerType.isExhausted !== undefined
            ? this._triggerType.isExhausted
            : false;
    }

    async getOsMap() {
        const _deviceEntityDef = this._gameEditor.getDeviceEntityClass();
        const val = await _deviceEntityDef.loadAndMergeOsMap(this._gameEditor._activeOrgId);
        this._osMap = val;
        return Promise.resolve();
    }

    async _getVendorMapDynamic() {
        const _deviceEntityDef = this._gameEditor.getDeviceEntityClass();
        this._vendorMap = await _deviceEntityDef.loadAndMergeVendorMap(
            this._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    async _getTypeBaseMapDynamic() {
        const _deviceEntityDef = this._gameEditor.getDeviceEntityClass();
        this._typeBaseMap = await _deviceEntityDef.loadAndMergeTypeBaseMap(
            this._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    async _getNodeProps() {
        this._nodeProps = await this._constructTypeEntityDef.getDeviceNodePropDefs(
            this._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    /*******************************************/
    /* END PUBLIC API ACTION METHODS           */
    /*******************************************/
}

WorkTriggerDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.router",
    "$teve.entities.ControlUser",
    "$teve.entities.CustomFields",
    "$teve.entities.Site",
    "$teve.entities.ConstructType",
];

WorkTriggerDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Details",
    icon: "worktrigger.edit",
    shown: true,
    title: "Details",
    order: 1,
};

module.exports = WorkTriggerDetailsViewComponent;
